/**
 * Reusable, data-driven ECharts toolkit.
 *
 * Each builder accepts a typed config object and returns a complete EChartsOption
 * ready to pass to <EChartsCard option={...} />.
 */
import type {EChartsOption} from "echarts";

/**
 * ECharts v6 types use strict discriminated unions that reject many
 * runtime-correct option literals (formatter callbacks, markArea tuples,
 * mixed series arrays). This helper isolates the cast to one place.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const asOption = (o: any): EChartsOption => o;

// ---------------------------------------------------------------------------
// 1A  Color palette & semantic colors
// ---------------------------------------------------------------------------

export const CHART_PALETTE = [
    "#209DB3",
    "#FF5100",
    "#5BF6C8",
    "#6366f1",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
    "#f97316",
    "#64748b",
] as const;

export const SEMANTIC_COLORS = {
    primary: "#209DB3",
    primaryDark: "#1A8C9E",
    accent: "#FF5100",
    mint: "#5BF6C8",
    positive: "#10B981",
    negative: "#DC2626",
    warning: "#f59e0b",
    neutral: "#94A3B8",
    dark: "#0F172A",
    muted: "#64748B",
    border: "#E2E8F0",
    background: "#F8FAFC",
} as const;

// ---------------------------------------------------------------------------
// 1B  Value formatters
// ---------------------------------------------------------------------------

export type ValueFormat =
    | "currency"
    | "compact_currency"
    | "percent"
    | "number"
    | "compact_number";

export function formatChartValue(value: number, format: ValueFormat): string {
    switch (format) {
        case "currency":
            return `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
        case "compact_currency": {
            const abs = Math.abs(value);
            if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
            if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
            if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
            return `$${value.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
        }
        case "percent":
            return `${value.toFixed(1)}%`;
        case "compact_number": {
            const abs = Math.abs(value);
            if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
            if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
            if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
            return value.toLocaleString(undefined, {maximumFractionDigits: 0});
        }
        case "number":
        default:
            return value.toLocaleString(undefined, {maximumFractionDigits: 0});
    }
}

// ---------------------------------------------------------------------------
// 1C  Shared helpers
// ---------------------------------------------------------------------------

export function axisLabelFormatter(format: ValueFormat): (value: number) => string {
    return (value: number) => formatChartValue(value, format);
}

export function tooltipFormatter(format: ValueFormat): (params: unknown) => string {
    return (params: unknown) => {
        if (Array.isArray(params)) {
            return params
                .map((p: {seriesName?: string; value?: number | null; color?: string}) => {
                    const dot = p.color
                        ? `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:4px"></span>`
                        : "";
                    const val = p.value != null ? formatChartValue(Number(p.value), format) : "-";
                    return `${dot}${p.seriesName ?? ""}: ${val}`;
                })
                .join("<br/>");
        }
        const p = params as {seriesName?: string; value?: number | null; name?: string};
        const val = p.value != null ? formatChartValue(Number(p.value), format) : "-";
        return `${p.name ?? p.seriesName ?? ""}: ${val}`;
    };
}

interface GridOverrides {
    left?: number | string;
    right?: number | string;
    top?: number | string;
    bottom?: number | string;
    containLabel?: boolean;
}

export function baseGrid(overrides?: GridOverrides) {
    return {
        left: 44,
        right: 16,
        top: 42,
        bottom: 30,
        ...overrides,
    };
}

interface TooltipOverrides {
    trigger?: "axis" | "item" | "none";
    axisPointer?: {type?: "shadow" | "line" | "cross" | "none"};
    formatter?: unknown;
    valueFormatter?: (value: number) => string;
}

export function baseTooltip(overrides?: TooltipOverrides) {
    return {
        trigger: "axis" as const,
        axisPointer: {type: "shadow" as const},
        backgroundColor: "#fff",
        borderColor: SEMANTIC_COLORS.border,
        borderWidth: 1,
        textStyle: {fontSize: 12, color: SEMANTIC_COLORS.dark},
        ...overrides,
    };
}

interface LegendOverrides {
    show?: boolean;
    top?: number | string;
    orient?: "horizontal" | "vertical";
    textStyle?: {fontSize?: number; color?: string};
}

export function baseLegend(overrides?: LegendOverrides) {
    return {
        top: 8,
        textStyle: {fontSize: 11},
        ...overrides,
    };
}

export function forecastBand(startCategory: string, endCategory: string) {
    return {
        itemStyle: {color: "rgba(250, 204, 21, 0.10)"},
        data: [[{xAxis: startCategory}, {xAxis: endCategory}]] as [[{xAxis: string}, {xAxis: string}]],
    };
}

export function paletteColor(index: number): string {
    return CHART_PALETTE[index % CHART_PALETTE.length];
}

// ---------------------------------------------------------------------------
// Internal axis/x-axis builders
// ---------------------------------------------------------------------------

function buildXAxis(categories: string[], boundaryGap = true) {
    return {
        type: "category" as const,
        data: categories,
        boundaryGap,
        axisTick: {show: false},
        axisLine: {lineStyle: {color: "#CBD5E1"}},
        axisLabel: {color: SEMANTIC_COLORS.muted, fontSize: 11},
    };
}

function buildYAxis(format: ValueFormat = "number", min?: number, max?: number, name?: string) {
    return {
        type: "value" as const,
        min,
        max,
        name,
        splitNumber: 5,
        nameTextStyle: {color: SEMANTIC_COLORS.muted, fontSize: 11},
        axisLabel: {
            color: SEMANTIC_COLORS.muted,
            fontSize: 11,
            formatter: axisLabelFormatter(format),
        },
        splitLine: {lineStyle: {color: SEMANTIC_COLORS.border, type: "dashed" as const}},
    };
}

// ---------------------------------------------------------------------------
// 1D  Chart builder functions
// ---------------------------------------------------------------------------

// ---- 1. Line Chart --------------------------------------------------------

export interface LineChartConfig {
    categories: string[];
    series: {
        key: string;
        label: string;
        data: (number | null)[];
        color?: string;
        dash?: boolean;
        areaStyle?: boolean;
    }[];
    yFormat?: ValueFormat;
    yMin?: number;
    yMax?: number;
    forecastStart?: string;
    smooth?: boolean;
}

export function buildLineChart(config: LineChartConfig): EChartsOption {
    const {categories, series, yFormat = "number", yMin, yMax, forecastStart, smooth = false} = config;
    const forecastEnd = forecastStart ? categories[categories.length - 1] : undefined;
    const mark = forecastStart && forecastEnd ? forecastBand(forecastStart, forecastEnd) : undefined;

    return asOption({
        color: series.map((s, i) => s.color ?? paletteColor(i)),
        grid: baseGrid(),
        tooltip: {
            ...baseTooltip(),
            valueFormatter: (value: number | null) =>
                value == null ? "-" : formatChartValue(Number(value), yFormat),
        },
        legend: baseLegend(),
        xAxis: buildXAxis(categories, false),
        yAxis: buildYAxis(yFormat, yMin, yMax),
        series: series.map((s, i) => ({
            name: s.label,
            type: "line" as const,
            smooth,
            data: s.data,
            symbol: "circle",
            symbolSize: 6,
            lineStyle: {width: 2, type: s.dash ? ("dashed" as const) : ("solid" as const)},
            areaStyle: s.areaStyle ? {opacity: 0.12} : undefined,
            color: s.color ?? paletteColor(i),
            markArea: mark && i === 0 ? mark : undefined,
        })),
    });
}

// ---- 2. Area Chart --------------------------------------------------------

export interface AreaChartConfig extends LineChartConfig {
    stacked?: boolean;
    opacity?: number;
}

export function buildAreaChart(config: AreaChartConfig): EChartsOption {
    const {stacked = false, opacity = 0.3, ...rest} = config;
    const areaSeries = rest.series.map((s) => ({
        ...s,
        areaStyle: true,
    }));
    const option = buildLineChart({...rest, series: areaSeries});

    // Apply stack + opacity to each series
    const patched = (option.series as Record<string, unknown>[]).map((s) => ({
        ...s,
        stack: stacked ? "area" : undefined,
        areaStyle: {opacity},
    }));
    return asOption({...option, series: patched});
}

// ---- 3. Bar Chart (grouped) -----------------------------------------------

export interface BarChartConfig {
    categories: string[];
    series: {key: string; label: string; data: number[]; color?: string}[];
    yFormat?: ValueFormat;
    horizontal?: boolean;
    barMaxWidth?: number;
    borderRadius?: number;
}

export function buildBarChart(config: BarChartConfig): EChartsOption {
    const {
        categories,
        series,
        yFormat = "number",
        horizontal = false,
        barMaxWidth = 30,
        borderRadius = 4,
    } = config;

    const catAxis = buildXAxis(categories);
    const valAxis = buildYAxis(yFormat);

    return asOption({
        color: series.map((s, i) => s.color ?? paletteColor(i)),
        grid: baseGrid(),
        tooltip: {
            ...baseTooltip(),
            valueFormatter: (value: number | null) =>
                value == null ? "-" : formatChartValue(Number(value), yFormat),
        },
        legend: baseLegend(),
        xAxis: horizontal ? valAxis : catAxis,
        yAxis: horizontal ? catAxis : valAxis,
        series: series.map((s, i) => ({
            name: s.label,
            type: "bar" as const,
            data: s.data,
            barMaxWidth,
            itemStyle: {
                color: s.color ?? paletteColor(i),
                borderRadius: horizontal
                    ? [0, borderRadius, borderRadius, 0]
                    : [borderRadius, borderRadius, 0, 0],
            },
        })),
    });
}

// ---- 4. Stacked Bar Chart -------------------------------------------------

export interface StackedBarChartConfig {
    categories: string[];
    series: {key: string; label: string; data: number[]; color?: string; stack?: string}[];
    yFormat?: ValueFormat;
    horizontal?: boolean;
}

export function buildStackedBarChart(config: StackedBarChartConfig): EChartsOption {
    const {categories, series, yFormat = "number", horizontal = false} = config;

    const catAxis = buildXAxis(categories);
    const valAxis = buildYAxis(yFormat);

    return asOption({
        color: series.map((s, i) => s.color ?? paletteColor(i)),
        grid: baseGrid(),
        tooltip: {
            ...baseTooltip(),
            valueFormatter: (value: number | null) =>
                value == null ? "-" : formatChartValue(Number(value), yFormat),
        },
        legend: baseLegend(),
        xAxis: horizontal ? valAxis : catAxis,
        yAxis: horizontal ? catAxis : valAxis,
        series: series.map((s, i) => ({
            name: s.label,
            type: "bar" as const,
            stack: s.stack ?? "default",
            data: s.data,
            itemStyle: {
                color: s.color ?? paletteColor(i),
                borderRadius: [4, 4, 0, 0],
            },
        })),
    });
}

// ---- 5. Dual-Axis Combo (bar + line) --------------------------------------

export interface ComboChartConfig {
    categories: string[];
    barSeries: {key: string; label: string; data: number[]; color?: string; stack?: string}[];
    lineSeries: {
        key: string;
        label: string;
        data: (number | null)[];
        color?: string;
        dash?: boolean;
        yAxisIndex?: number;
    }[];
    yFormat?: ValueFormat;
    y2Format?: ValueFormat;
    forecastStart?: string;
}

export function buildComboChart(config: ComboChartConfig): EChartsOption {
    const {categories, barSeries, lineSeries, yFormat = "number", y2Format, forecastStart} = config;

    const hasSecondAxis = y2Format != null || lineSeries.some((s) => s.yAxisIndex === 1);
    const forecastEnd = forecastStart ? categories[categories.length - 1] : undefined;
    const mark = forecastStart && forecastEnd ? forecastBand(forecastStart, forecastEnd) : undefined;

    const allColors = [
        ...barSeries.map((s, i) => s.color ?? paletteColor(i)),
        ...lineSeries.map((s, i) => s.color ?? paletteColor(barSeries.length + i)),
    ];

    return asOption({
        color: allColors,
        grid: baseGrid(),
        tooltip: {
            ...baseTooltip(),
            valueFormatter: (value: number | null) =>
                value == null ? "-" : formatChartValue(Number(value), yFormat),
        },
        legend: baseLegend(),
        xAxis: buildXAxis(categories),
        yAxis: hasSecondAxis
            ? [
                  buildYAxis(yFormat),
                  {
                      ...buildYAxis(y2Format ?? yFormat),
                      splitLine: {show: false},
                  },
              ]
            : buildYAxis(yFormat),
        series: [
            ...barSeries.map((s, i) => ({
                name: s.label,
                type: "bar" as const,
                yAxisIndex: 0,
                data: s.data,
                stack: s.stack,
                barMaxWidth: 30,
                itemStyle: {
                    color: s.color ?? paletteColor(i),
                    borderRadius: [4, 4, 0, 0],
                },
            })),
            ...lineSeries.map((s, i) => ({
                name: s.label,
                type: "line" as const,
                yAxisIndex: s.yAxisIndex ?? (hasSecondAxis ? 1 : 0),
                data: s.data,
                smooth: true,
                symbol: "circle",
                symbolSize: 6,
                color: s.color ?? paletteColor(barSeries.length + i),
                lineStyle: {
                    width: 2.4,
                    type: s.dash ? ("dashed" as const) : ("solid" as const),
                },
                markArea: mark && i === 0 ? mark : undefined,
            })),
        ],
    });
}

// ---- 6. Waterfall / Bridge Chart ------------------------------------------

export interface WaterfallChartConfig {
    categories: string[];
    values: number[];
    totalIndices?: number[];
    colors?: {positive: string; negative: string; total: string};
    yFormat?: ValueFormat;
}

export function buildWaterfallChart(config: WaterfallChartConfig): EChartsOption {
    const {
        categories,
        values,
        totalIndices = [],
        colors = {
            positive: SEMANTIC_COLORS.positive,
            negative: SEMANTIC_COLORS.negative,
            total: SEMANTIC_COLORS.primary,
        },
        yFormat = "compact_currency",
    } = config;

    const totalSet = new Set(totalIndices);
    const assist: (number | "-")[] = [];
    const positive: (number | "-")[] = [];
    const negative: (number | "-")[] = [];
    const totals: (number | "-")[] = [];
    let running = 0;

    for (let idx = 0; idx < values.length; idx++) {
        const v = values[idx];
        if (totalSet.has(idx)) {
            assist.push(0);
            positive.push("-");
            negative.push("-");
            totals.push(v);
            running = v;
            continue;
        }

        const next = running + v;
        totals.push("-");
        if (v >= 0) {
            assist.push(running);
            positive.push(v);
            negative.push("-");
        } else {
            assist.push(next);
            positive.push("-");
            negative.push(Math.abs(v));
        }
        running = next;
    }

    return asOption({
        grid: baseGrid(),
        tooltip: {
            ...baseTooltip(),
            formatter: (params: {seriesName: string; dataIndex: number; value: number | string}[]) => {
                const row = params.find((p) => p.seriesName !== "Offset" && p.value !== "-");
                if (!row) return "";
                const label = categories[row.dataIndex];
                const rawVal = values[row.dataIndex];
                return `${label}<br/>${formatChartValue(rawVal, yFormat)}`;
            },
        },
        legend: {show: false},
        xAxis: buildXAxis(categories),
        yAxis: buildYAxis(yFormat),
        series: [
            {
                name: "Offset",
                type: "bar" as const,
                stack: "bridge",
                data: assist,
                itemStyle: {color: "transparent"},
                emphasis: {disabled: true},
                tooltip: {show: false},
            },
            {
                name: "Increase",
                type: "bar" as const,
                stack: "bridge",
                data: positive,
                itemStyle: {color: colors.positive, borderRadius: [4, 4, 0, 0]},
            },
            {
                name: "Decrease",
                type: "bar" as const,
                stack: "bridge",
                data: negative,
                itemStyle: {color: colors.negative, borderRadius: [4, 4, 0, 0]},
            },
            {
                name: "Total",
                type: "bar" as const,
                data: totals,
                itemStyle: {color: colors.total, borderRadius: [4, 4, 0, 0]},
            },
        ],
    });
}

// ---- 7. Pie Chart ---------------------------------------------------------

export interface PieChartConfig {
    segments: {name: string; value: number; color?: string}[];
    showLabels?: boolean;
    labelFormat?: ValueFormat;
}

export function buildPieChart(config: PieChartConfig): EChartsOption {
    const {segments, showLabels = false, labelFormat = "compact_currency"} = config;
    const colors = segments.map((s, i) => s.color ?? paletteColor(i));

    return asOption({
        color: colors,
        tooltip: {
            ...baseTooltip({trigger: "item"}),
            formatter: (params: {name: string; value: number; percent: number}) =>
                `${params.name}<br/>${formatChartValue(params.value, labelFormat)} (${params.percent.toFixed(1)}%)`,
        },
        legend: baseLegend(),
        series: [
            {
                type: "pie" as const,
                radius: "75%",
                center: ["50%", "55%"],
                avoidLabelOverlap: true,
                itemStyle: {borderRadius: 4, borderColor: "#fff", borderWidth: 1},
                label: showLabels
                    ? {formatter: "{b}\n{d}%", fontSize: 11}
                    : {show: false},
                emphasis: {label: {show: true, formatter: "{b}\n{d}%", fontSize: 11}},
                data: segments.map((s) => ({
                    name: s.name,
                    value: s.value,
                    itemStyle: s.color ? {color: s.color} : undefined,
                })),
            },
        ],
    });
}

// ---- 8. Donut Chart -------------------------------------------------------

export interface DonutChartConfig extends PieChartConfig {
    innerRadius?: string;
    centerLabel?: string;
    centerValue?: string;
}

export function buildDonutChart(config: DonutChartConfig): EChartsOption {
    const {innerRadius = "55%", centerLabel, centerValue, ...pieConfig} = config;
    const option = buildPieChart(pieConfig);

    // Patch radius to create donut
    const series = option.series as Record<string, unknown>[];
    series[0].radius = [innerRadius, "75%"];
    series[0].center = ["50%", "45%"];

    // Add center text
    const graphic: Record<string, unknown>[] = [];
    if (centerValue) {
        graphic.push({
            type: "text",
            left: "center",
            top: "38%",
            style: {
                text: centerValue,
                textAlign: "center",
                fill: SEMANTIC_COLORS.dark,
                fontSize: 14,
                fontWeight: 700,
            },
        });
    }
    if (centerLabel) {
        graphic.push({
            type: "text",
            left: "center",
            top: centerValue ? "48%" : "42%",
            style: {
                text: centerLabel,
                textAlign: "center",
                fill: SEMANTIC_COLORS.muted,
                fontSize: 11,
            },
        });
    }

    return asOption({
        ...option,
        legend: {
            ...baseLegend(),
            orient: "horizontal" as const,
            bottom: 0,
        },
        graphic: graphic.length > 0 ? graphic : undefined,
    });
}

// ---- 9. Heatmap -----------------------------------------------------------

export interface HeatmapChartConfig {
    xCategories: string[];
    yCategories: string[];
    data: [number, number, number][];
    valueFormat?: ValueFormat;
    minColor?: string;
    maxColor?: string;
    min?: number;
    max?: number;
}

export function buildHeatmapChart(config: HeatmapChartConfig): EChartsOption {
    const {
        xCategories,
        yCategories,
        data,
        valueFormat = "number",
        minColor = "#e0f2fe",
        maxColor = SEMANTIC_COLORS.primary,
        min,
        max,
    } = config;

    const computedMin = min ?? Math.min(...data.map((d) => d[2]));
    const computedMax = max ?? Math.max(...data.map((d) => d[2]));

    return asOption({
        grid: baseGrid({top: 16, bottom: 60}),
        tooltip: {
            ...baseTooltip({trigger: "item"}),
            formatter: (params: {data: [number, number, number]}) => {
                const [xi, yi, val] = params.data;
                return `${xCategories[xi]} / ${yCategories[yi]}<br/>${formatChartValue(val, valueFormat)}`;
            },
        },
        xAxis: {
            type: "category" as const,
            data: xCategories,
            axisTick: {show: false},
            axisLabel: {color: SEMANTIC_COLORS.muted, fontSize: 11},
        },
        yAxis: {
            type: "category" as const,
            data: yCategories,
            axisTick: {show: false},
            axisLabel: {color: SEMANTIC_COLORS.muted, fontSize: 11},
        },
        visualMap: {
            min: computedMin,
            max: computedMax,
            calculable: true,
            orient: "horizontal" as const,
            left: "center",
            bottom: 4,
            inRange: {color: [minColor, maxColor]},
            textStyle: {fontSize: 11},
        },
        series: [
            {
                type: "heatmap" as const,
                data,
                label: {show: true, formatter: (p: {data: [number, number, number]}) => formatChartValue(p.data[2], valueFormat), fontSize: 10},
                emphasis: {itemStyle: {shadowBlur: 10, shadowColor: "rgba(0,0,0,0.15)"}},
            },
        ],
    });
}

// ---- 10. Scatter Chart ----------------------------------------------------

export interface ScatterChartConfig {
    series: {
        key: string;
        label: string;
        data: [number, number][];
        color?: string;
        symbolSize?: number;
    }[];
    xFormat?: ValueFormat;
    yFormat?: ValueFormat;
    xLabel?: string;
    yLabel?: string;
}

export function buildScatterChart(config: ScatterChartConfig): EChartsOption {
    const {series, xFormat = "number", yFormat = "number", xLabel, yLabel} = config;

    return asOption({
        color: series.map((s, i) => s.color ?? paletteColor(i)),
        grid: baseGrid({left: 56}),
        tooltip: {
            ...baseTooltip({trigger: "item"}),
            formatter: (params: {seriesName: string; data: [number, number]}) => {
                const [x, y] = params.data;
                return `${params.seriesName}<br/>${xLabel ?? "X"}: ${formatChartValue(x, xFormat)}<br/>${yLabel ?? "Y"}: ${formatChartValue(y, yFormat)}`;
            },
        },
        legend: baseLegend(),
        xAxis: {
            type: "value" as const,
            name: xLabel,
            nameTextStyle: {color: SEMANTIC_COLORS.muted, fontSize: 11},
            axisLabel: {
                color: SEMANTIC_COLORS.muted,
                fontSize: 11,
                formatter: axisLabelFormatter(xFormat),
            },
            splitLine: {lineStyle: {color: SEMANTIC_COLORS.border, type: "dashed" as const}},
        },
        yAxis: {
            type: "value" as const,
            name: yLabel,
            nameTextStyle: {color: SEMANTIC_COLORS.muted, fontSize: 11},
            axisLabel: {
                color: SEMANTIC_COLORS.muted,
                fontSize: 11,
                formatter: axisLabelFormatter(yFormat),
            },
            splitLine: {lineStyle: {color: SEMANTIC_COLORS.border, type: "dashed" as const}},
        },
        series: series.map((s, i) => ({
            name: s.label,
            type: "scatter" as const,
            data: s.data,
            symbolSize: s.symbolSize ?? 10,
            color: s.color ?? paletteColor(i),
        })),
    });
}

// ---- 11. Radar Chart ------------------------------------------------------

export interface RadarChartConfig {
    indicators: {name: string; max: number}[];
    series: {label: string; data: number[]; color?: string; areaStyle?: boolean}[];
}

export function buildRadarChart(config: RadarChartConfig): EChartsOption {
    const {indicators, series} = config;

    return asOption({
        color: series.map((s, i) => s.color ?? paletteColor(i)),
        tooltip: baseTooltip({trigger: "item"}),
        legend: baseLegend(),
        radar: {
            indicator: indicators,
            shape: "polygon" as const,
            splitArea: {areaStyle: {color: ["#fff", SEMANTIC_COLORS.background]}},
            axisName: {color: SEMANTIC_COLORS.muted, fontSize: 11},
        },
        series: [
            {
                type: "radar" as const,
                data: series.map((s, i) => ({
                    name: s.label,
                    value: s.data,
                    areaStyle: s.areaStyle ? {opacity: 0.15} : undefined,
                    lineStyle: {color: s.color ?? paletteColor(i)},
                    itemStyle: {color: s.color ?? paletteColor(i)},
                })),
            },
        ],
    });
}

// ---- Builder Dispatch -----------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BuilderFn = (config: any) => EChartsOption;

export function buildChartFromBuilder(builder: string, config: Record<string, unknown>): EChartsOption | null {
    const BUILDERS: Record<string, BuilderFn> = {
        line: buildLineChart,
        area: buildAreaChart,
        bar: buildBarChart,
        stacked_bar: buildStackedBarChart,
        combo: buildComboChart,
        waterfall: buildWaterfallChart,
        pie: buildPieChart,
        donut: buildDonutChart,
        heatmap: buildHeatmapChart,
        scatter: buildScatterChart,
        radar: buildRadarChart,
        treemap: buildTreemapChart,
    };
    const fn = BUILDERS[builder];
    if (!fn) return null;
    try {
        return fn(config);
    } catch {
        return null;
    }
}

// ---- 12. Treemap ----------------------------------------------------------

export interface TreemapChartConfig {
    data: {name: string; value: number; children?: TreemapChartConfig["data"]; color?: string}[];
    valueFormat?: ValueFormat;
    levels?: number;
}

export function buildTreemapChart(config: TreemapChartConfig): EChartsOption {
    const {data, valueFormat = "compact_currency", levels = 2} = config;

    const levelConfigs = Array.from({length: levels}, (_, i) => ({
        itemStyle: {
            borderColor: "#fff",
            borderWidth: i === 0 ? 2 : 1,
            gapWidth: i === 0 ? 2 : 1,
        },
    }));

    return asOption({
        color: CHART_PALETTE.slice(),
        tooltip: {
            ...baseTooltip({trigger: "item"}),
            formatter: (params: {name: string; value: number}) =>
                `${params.name}<br/>${formatChartValue(params.value, valueFormat)}`,
        },
        series: [
            {
                type: "treemap" as const,
                data: data.map((d) => ({
                    name: d.name,
                    value: d.value,
                    children: d.children,
                    itemStyle: d.color ? {color: d.color} : undefined,
                })),
                levels: levelConfigs,
                label: {show: true, formatter: "{b}", fontSize: 11},
                breadcrumb: {show: false},
            },
        ],
    });
}
